import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardBody,
  Button,
  Chip,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Textarea,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  getAIExcelExamples,
  updateExampleStatus,
  deleteExample,
  downloadExampleFile,
  type ReceiverGroup,
  type AIExcelExample,
} from 'common/src/api/ai-excel-examples';
import {
  generateRobot,
  type RobotExcelExample,
} from 'common/src/api/generate-robot';
import {
  Alert,
  EmptyState,
  LoadingState,
  PageHeader,
  PageLayout,
} from '../../../components/ui';

const sourceLabels: Record<string, string> = {
  no_robot: 'Sin Robot',
  robot_failed: 'Robot Falló',
  no_match: 'Sin Match',
};

const sourceColors: Record<string, 'warning' | 'danger' | 'default'> = {
  no_robot: 'warning',
  robot_failed: 'danger',
  no_match: 'default',
};

const statusColors: Record<string, 'success' | 'warning' | 'danger'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

const statusLabels: Record<string, string> = {
  approved: 'Aprobado',
  pending: 'Pendiente',
  rejected: 'Rechazado',
};

const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:...;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const SuperadminAIExamples = () => {
  const [groups, setGroups] = useState<ReceiverGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceiver, setSelectedReceiver] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [fixExplanation, setFixExplanation] = useState('');
  const [extraInstructions, setExtraInstructions] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await getAIExcelExamples();
      setGroups(data);
    } catch (error: any) {
      toast.error('Error al cargar ejemplos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.receiver_name === selectedReceiver) || null,
    [groups, selectedReceiver]
  );

  const handleStatusChange = async (
    id: string,
    status: 'approved' | 'rejected'
  ) => {
    try {
      await updateExampleStatus(id, status);
      toast.success(`Ejemplo ${statusLabels[status].toLowerCase()}`);
      await fetchData();
    } catch (error: any) {
      toast.error('Error al actualizar estado');
      console.error(error);
    }
  };

  const handleDelete = async (example: AIExcelExample) => {
    try {
      await deleteExample(example.id, example.storage_path);
      toast.success('Ejemplo eliminado');
      await fetchData();
      selectedIds.delete(example.id);
      setSelectedIds(new Set(selectedIds));
    } catch (error: any) {
      toast.error('Error al eliminar ejemplo');
      console.error(error);
    }
  };

  const handleDownload = async (example: AIExcelExample) => {
    if (!example.storage_path) return;
    try {
      const blob = await downloadExampleFile(example.storage_path);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = example.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error('Error al descargar archivo');
      console.error(error);
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (!selectedGroup) return;
    if (selectedIds.size === selectedGroup.examples.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectedGroup.examples.map((e) => e.id)));
    }
  };

  const handleGenerate = async () => {
    if (!selectedGroup || selectedIds.size === 0) return;

    const selected = selectedGroup.examples.filter((e) => selectedIds.has(e.id));

    // Must have both storage_path AND ai_output
    const valid = selected.filter((e) => e.storage_path && e.ai_output);
    const skipped = selected.length - valid.length;

    if (valid.length === 0) {
      toast.error(
        'Ningun ejemplo seleccionado tiene archivo y ai_output asociados'
      );
      return;
    }

    if (skipped > 0) {
      toast.warning(
        `${skipped} ejemplo(s) omitido(s) por no tener archivo o ai_output`
      );
    }

    // Cap at 3 examples
    let usedExamples = valid;
    if (usedExamples.length > 3) {
      toast.warning(`Usando solo los primeros 3 de ${usedExamples.length} ejemplos`);
      usedExamples = usedExamples.slice(0, 3);
    }

    setGenerating(true);
    setGeneratedCode('');
    setFixExplanation('');

    try {
      const examples: RobotExcelExample[] = [];

      for (const example of usedExamples) {
        const blob = await downloadExampleFile(example.storage_path!);
        const base64Data = await blobToBase64(blob);
        examples.push({
          base64Data,
          selectedSheet: '',
          filename: example.filename,
          aiOutput: example.ai_output,
        });
      }

      const result = await generateRobot({
        receiverName: selectedGroup.receiver_name,
        examples,
        extraInstructions: extraInstructions.trim() || undefined,
      });

      if (result.success && result.code) {
        setGeneratedCode(result.code);
        if (result.fixExplanation) {
          setFixExplanation(result.fixExplanation);
        }
        toast.success('Codigo generado correctamente');
      } else {
        throw new Error(result.error || 'Error desconocido');
      }
    } catch (error: any) {
      console.error('Error generating:', error);
      toast.error(error.message || 'Error al generar el robot');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
      toast.success('Codigo copiado al portapapeles');
    } catch {
      toast.error('Error al copiar');
    }
  };

  const handleBack = () => {
    setSelectedReceiver(null);
    setSelectedIds(new Set());
    setGeneratedCode('');
    setFixExplanation('');
    setExtraInstructions('');
  };

  if (loading) {
    return (
      <PageLayout>
        <LoadingState />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {selectedReceiver && (
        <Button
          variant='light'
          size='sm'
          className='mb-4 w-fit'
          startContent={<Icon icon='tabler:arrow-left' />}
          onPress={handleBack}
        >
          Volver
        </Button>
      )}
      <PageHeader
        title={
          selectedReceiver
            ? `Ejemplos: ${selectedReceiver}`
            : 'Biblioteca de Ejemplos AI'
        }
        actions={
          selectedReceiver ? (
            <Chip variant='flat' size='lg'>
              {selectedGroup?.total || 0} ejemplos
            </Chip>
          ) : undefined
        }
      />

      {/* Receiver Cards Grid */}
      {!selectedReceiver && (
        <>
          {groups.length === 0 ? (
            <EmptyState
              icon='tabler:database-off'
              title='No hay ejemplos guardados aun'
            />
          ) : (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {groups.map((group) => (
                <Card
                  key={group.receiver_name}
                  isPressable
                  onPress={() => setSelectedReceiver(group.receiver_name)}
                  shadow='sm'
                >
                  <CardBody className='space-y-3'>
                    <div className='flex justify-between items-start'>
                      <h3 className='text-lg font-semibold'>
                        {group.receiver_name}
                      </h3>
                      <Chip size='sm' variant='flat'>
                        {group.total}
                      </Chip>
                    </div>

                    <div className='flex gap-2 flex-wrap'>
                      {group.pending > 0 && (
                        <Chip size='sm' color='warning' variant='flat'>
                          {group.pending} pendientes
                        </Chip>
                      )}
                      {group.approved > 0 && (
                        <Chip size='sm' color='success' variant='flat'>
                          {group.approved} aprobados
                        </Chip>
                      )}
                      {group.rejected > 0 && (
                        <Chip size='sm' color='danger' variant='flat'>
                          {group.rejected} rechazados
                        </Chip>
                      )}
                    </div>

                    <div className='flex gap-2 flex-wrap'>
                      {group.sources.no_robot > 0 && (
                        <Chip size='sm' variant='dot' color='warning'>
                          Sin Robot: {group.sources.no_robot}
                        </Chip>
                      )}
                      {group.sources.robot_failed > 0 && (
                        <Chip size='sm' variant='dot' color='danger'>
                          Robot Falló: {group.sources.robot_failed}
                        </Chip>
                      )}
                      {group.sources.no_match > 0 && (
                        <Chip size='sm' variant='dot'>
                          Sin Match: {group.sources.no_match}
                        </Chip>
                      )}
                    </div>

                    <Button
                      size='sm'
                      variant='flat'
                      className='w-full'
                      endContent={<Icon icon='tabler:chevron-right' width={16} />}
                    >
                      Ver ejemplos
                    </Button>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Examples Table */}
      {selectedReceiver && selectedGroup && (
        <>
          <Card className='mb-6'>
            <CardBody>
              <Table
                aria-label='Ejemplos AI Excel'
                removeWrapper
              >
                <TableHeader>
                  <TableColumn width={50}>
                    <Checkbox
                      isSelected={
                        selectedGroup.examples.length > 0 &&
                        selectedIds.size === selectedGroup.examples.length
                      }
                      isIndeterminate={
                        selectedIds.size > 0 &&
                        selectedIds.size < selectedGroup.examples.length
                      }
                      onValueChange={toggleSelectAll}
                    />
                  </TableColumn>
                  <TableColumn>Archivo</TableColumn>
                  <TableColumn width={130}>Origen</TableColumn>
                  <TableColumn width={130}>Estado</TableColumn>
                  <TableColumn width={180}>Fecha</TableColumn>
                  <TableColumn width={150}>Acciones</TableColumn>
                </TableHeader>
                <TableBody emptyContent='No hay ejemplos'>
                  {selectedGroup.examples.map((example) => (
                    <TableRow key={example.id}>
                      <TableCell>
                        <Checkbox
                          isSelected={selectedIds.has(example.id)}
                          onValueChange={() => toggleSelect(example.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <Icon
                            icon='tabler:file-spreadsheet'
                            width={18}
                            className='text-green-600 shrink-0'
                          />
                          <span className='text-sm truncate max-w-[300px]'>
                            {example.filename}
                          </span>
                          {!example.storage_path && (
                            <Chip size='sm' variant='flat' color='default'>
                              Sin archivo
                            </Chip>
                          )}
                          {!example.ai_output && (
                            <Chip size='sm' variant='flat' color='danger'>
                              Sin AI output
                            </Chip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='sm'
                          color={sourceColors[example.source]}
                          variant='flat'
                        >
                          {sourceLabels[example.source]}
                        </Chip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='sm'
                          color={statusColors[example.status]}
                          variant='flat'
                        >
                          {statusLabels[example.status]}
                        </Chip>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm text-default-500'>
                          {new Date(example.created_at).toLocaleDateString(
                            'es-CL',
                            {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            }
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className='flex gap-1'>
                          {example.storage_path && (
                            <Button
                              isIconOnly
                              size='sm'
                              variant='light'
                              color='primary'
                              onPress={() => handleDownload(example)}
                              title='Descargar Excel'
                            >
                              <Icon icon='tabler:download' width={16} />
                            </Button>
                          )}
                          {example.status !== 'approved' && (
                            <Button
                              isIconOnly
                              size='sm'
                              variant='light'
                              color='success'
                              onPress={() =>
                                handleStatusChange(example.id, 'approved')
                              }
                              title='Aprobar'
                            >
                              <Icon icon='tabler:check' width={16} />
                            </Button>
                          )}
                          {example.status !== 'rejected' && (
                            <Button
                              isIconOnly
                              size='sm'
                              variant='light'
                              color='warning'
                              onPress={() =>
                                handleStatusChange(example.id, 'rejected')
                              }
                              title='Rechazar'
                            >
                              <Icon icon='tabler:x' width={16} />
                            </Button>
                          )}
                          <Button
                            isIconOnly
                            size='sm'
                            variant='light'
                            color='danger'
                            onPress={() => handleDelete(example)}
                            title='Eliminar'
                          >
                            <Icon icon='tabler:trash' width={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardBody>
          </Card>

          {/* Generate Robot Section */}
          <Card className='mb-6'>
            <CardBody className='space-y-4'>
              <h2 className='text-xl font-semibold'>Generar Robot</h2>
              <p className='text-sm text-default-500'>
                Selecciona ejemplos de la tabla y genera una funcion de
                integracion. Se descargan los archivos seleccionados y se envian
                al generador de IA.
              </p>

              <Textarea
                label='Instrucciones adicionales (opcional)'
                placeholder='Ej: La columna H tiene el formato y peso combinados...'
                value={extraInstructions}
                onChange={(e) => setExtraInstructions(e.target.value)}
                minRows={2}
                maxRows={4}
              />

              <div className='flex items-center justify-between'>
                <span className='text-sm text-default-500'>
                  {selectedIds.size} ejemplo{selectedIds.size !== 1 ? 's' : ''}{' '}
                  seleccionado{selectedIds.size !== 1 ? 's' : ''}
                </span>
                <Button
                  color='primary'
                  onPress={handleGenerate}
                  isLoading={generating}
                  isDisabled={selectedIds.size === 0}
                  startContent={
                    !generating ? (
                      <Icon icon='tabler:robot' width={18} />
                    ) : undefined
                  }
                >
                  Generar Robot
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Fix Explanation */}
          {fixExplanation && (
            <Card className='mb-6'>
              <CardBody>
                <h2 className='text-xl font-semibold mb-3'>Diagnostico del Fix</h2>
                <Alert variant='warning' className='whitespace-pre-wrap'>
                  {fixExplanation}
                </Alert>
              </CardBody>
            </Card>
          )}

          {/* Generated Code Output */}
          {generatedCode && (
            <Card className='mb-6'>
              <CardBody>
                <div className='flex justify-between items-center mb-4'>
                  <h2 className='text-xl font-semibold'>Codigo Generado</h2>
                  <Button
                    size='sm'
                    variant='flat'
                    onPress={handleCopy}
                    startContent={<Icon icon='tabler:clipboard' width={16} />}
                  >
                    Copiar
                  </Button>
                </div>
                <pre className='bg-default-100 text-foreground p-4 rounded-lg overflow-x-auto text-sm leading-relaxed'>
                  <code>{generatedCode}</code>
                </pre>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default SuperadminAIExamples;
